// Exchange-rate page: the per-bank rate columns and the Excel export of a
// date range. Rates live at ExchangeRateCenter/FX-Rate/{bankCode}/{YYYY-MM-DD},
// one doc per day, one field per currency.

import moment from "moment";

// One year of daily docs is ~365 reads per export; past that, ask for a
// shorter range instead.
export const MAX_RANGE_DAYS = 366;

// Banks with a scraper writing to ExchangeRateCenter/FX-Rate/{bankCode}, in
// the order the page lists them.
export const EXCHANGE_BANKS = [
  { bankName: "Bank of Thailand", bankCode: "BOT", bankLogoUrl: "/images/banks/bot.png" },
  { bankName: "KasikornBank", bankCode: "KBANK", bankLogoUrl: "/images/banks/kbank.png" },
  { bankName: "Siam Commercial Bank", bankCode: "SCB", bankLogoUrl: "/images/banks/scb.png" },
  { bankName: "Bangkok Bank", bankCode: "BBL", bankLogoUrl: "/images/banks/bbl.png" },
  { bankName: "Krungthai Bank", bankCode: "KTB", bankLogoUrl: "/images/banks/ktb.png" },
  {
    bankName: "Banque Pour Le Commerce Exterieur Lao Public",
    bankCode: "BCEL",
    bankLogoUrl: "/images/banks/bcel.png",
  },
];

// The banks a company shows on the page. `saved` is the bank codes from
// Companies/{id}/ExchangeRate/setting.visibleBanks; never set, or nothing
// known left in it, shows every bank. Order is always EXCHANGE_BANKS's.
export function visibleBanks(saved) {
  const codes = Array.isArray(saved) ? saved : [];
  const shown = EXCHANGE_BANKS.filter((b) => codes.indexOf(b.bankCode) !== -1);
  return shown.length ? shown : EXCHANGE_BANKS;
}

// The history page (ประวัติอัตราแลกเปลี่ยน) shows unless the company turned it
// off: never set means on.
export function historyPageEnabled(setting) {
  return !(setting && setting.showHistoryPage === false);
}

// Columns the page shows for each bank, in order. `active` marks the column
// rendered bold on screen.
const BANK_COLUMNS = {
  BOT: [
    { colName: "Sight Bill", fieldName: "BuyingEXP", type: "BUYING" },
    { colName: "Transfer", fieldName: "BuyingTRN", type: "BUYING", active: true },
    { colName: "Average Selling Rates", fieldName: "SellingNotes", type: "SELLING", active: true },
  ],
  KBANK: [
    { colName: "TRAVELLERS' CHEQUES", fieldName: "BuyingCHQ", type: "BUYING" },
    { colName: "EXPORT SIGHT BILL", fieldName: "BuyingEXP", type: "BUYING" },
    { colName: "TELEX TRANSFER", fieldName: "BuyingTRN", type: "BUYING" },
    { colName: "BANK NOTES", fieldName: "BuyingNotes", type: "BUYING", active: true },
    { colName: "TT&DRAFT T/CHEQUES", fieldName: "SellingCHQ", type: "SELLING" },
    { colName: "BANK NOTES", fieldName: "SellingNotes", type: "SELLING", active: true },
  ],
  BBL: [
    { colName: "Sight Bill", fieldName: "BuyingEXP", type: "BUYING" },
    { colName: "TT", fieldName: "BuyingTRN", type: "BUYING" },
    { colName: "Bank Notes", fieldName: "BuyingNotes", type: "BUYING", active: true },
    { colName: "Bill - DD - TT", fieldName: "SellingCHQ", type: "SELLING" },
    { colName: "Bank Notes", fieldName: "SellingNotes", type: "SELLING", active: true },
  ],
  KTB: [
    { colName: "SIGHT BILL", fieldName: "BuyingEXP", type: "BUYING" },
    { colName: "TT", fieldName: "BuyingTRN", type: "BUYING" },
    { colName: "FOREIGN BANK NOTE", fieldName: "BuyingNotes", type: "BUYING", active: true },
    { colName: "T/T", fieldName: "SellingCHQ", type: "SELLING" },
    { colName: "FOREIGN BANK NOTE", fieldName: "SellingNotes", type: "SELLING", active: true },
  ],
  SCB: [
    { colName: "T/CHQS. & CHQS.", fieldName: "BuyingCHQ", type: "BUYING" },
    { colName: "EXPORT SIGHT BILL", fieldName: "BuyingEXP", type: "BUYING" },
    { colName: "TT", fieldName: "BuyingTRN", type: "BUYING" },
    { colName: "NOTE", fieldName: "BuyingNotes", type: "BUYING", active: true },
    { colName: "D/D & T/T", fieldName: "SellingCHQ", type: "SELLING" },
    { colName: "NOTE", fieldName: "SellingNotes", type: "SELLING", active: true },
  ],
  BCEL: [
    { colName: "BILL", fieldName: "BuyingEXP", type: "BUYING" },
    // EFT is the rate SAP books against (see the Oct 2026 BCEL/SAP check).
    { colName: "EFT", fieldName: "BuyingTRN", type: "BUYING", active: true },
    { colName: "NOTE", fieldName: "BuyingNotes", type: "BUYING" },
    { colName: "Sell Rates", fieldName: "SellingNotes", type: "SELLING", active: true },
  ],
};

// type: "BUYING" | "SELLING" filters; anything else returns every column.
export function bankColumns(bankCode, type) {
  const columns = BANK_COLUMNS[bankCode] || [];
  return type === "BUYING" || type === "SELLING"
    ? columns.filter((c) => c.type === type)
    : columns;
}

export function rateUnit(bankCode) {
  return bankCode === "BCEL" ? "KIP" : "THB";
}

// The column a side is usually read from: its first `active` one, else its
// first one.
export function primaryColumn(bankCode, type) {
  const columns = bankColumns(bankCode, type);
  return columns.find((c) => c.active) || columns[0] || null;
}

// Currencies shown as cards above the table, in this order, when present.
export function highlightCodes(bankCode) {
  return bankCode === "BCEL" ? ["USD", "THB", "EUR", "CNY"] : ["USD", "EUR", "JPY", "CNY"];
}

// Change between two printed rates. direction is "up" | "down" | "flat", or
// null when either side has no rate. text is the absolute change, printed
// with the decimals of the current rate.
export function rateChange(current, previous) {
  const now = parseRate(current);
  const before = parseRate(previous);
  if (now === null || before === null) return { direction: null, text: "" };
  const decimals = (String(current).split(".")[1] || "").length;
  const delta = Number((now - before).toFixed(decimals));
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const text = Math.abs(delta).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return { direction, text };
}

const THAI_MONTHS = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];

// "2026-10-28" -> "28 ต.ค. 2026" (Gregorian year, as the rest of the page).
export function formatThaiDate(isoDate) {
  const m = moment(isoDate, "YYYY-MM-DD", true);
  return m.isValid() ? `${m.date()} ${THAI_MONTHS[m.month()]} ${m.year()}` : "";
}

// Export dialog quick ranges, relative to `today` (YYYY-MM-DD).
export function datePresets(today) {
  const t = moment(today, "YYYY-MM-DD");
  const lastMonth = t.clone().subtract(1, "month");
  return [
    { key: "thisMonth", label: "เดือนนี้", from: t.clone().startOf("month"), to: t.clone() },
    {
      key: "lastMonth",
      label: "เดือนที่แล้ว",
      from: lastMonth.clone().startOf("month"),
      to: lastMonth.clone().endOf("month"),
    },
    { key: "last30", label: "30 วันล่าสุด", from: t.clone().subtract(29, "days"), to: t.clone() },
  ].map((p) => ({ key: p.key, label: p.label, from: p.from.format("YYYY-MM-DD"), to: p.to.format("YYYY-MM-DD") }));
}

// Days between from and to (inclusive) with no doc id in `ids`.
export function missingDates(from, to, ids) {
  if (rangeError(from, to)) return [];
  const have = new Set(ids);
  const missing = [];
  for (let d = moment(from, "YYYY-MM-DD"); d.format("YYYY-MM-DD") <= to; d.add(1, "day")) {
    const id = d.format("YYYY-MM-DD");
    if (!have.has(id)) missing.push(id);
  }
  return missing;
}

// The scrapers store rates as the bank prints them: "22,316", "0.8558",
// "-" or "" for no rate. Excel needs numbers.
export function parseRate(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value === null || value === undefined) return null;
  const n = Number(String(value).replace(/,/g, "").trim());
  return String(value).trim() !== "" && Number.isFinite(n) ? n : null;
}

// null when the range is usable, otherwise the Thai message to show.
export function rangeError(from, to) {
  const start = moment(from, "YYYY-MM-DD", true);
  const end = moment(to, "YYYY-MM-DD", true);
  if (!start.isValid() || !end.isValid()) return "กรุณาเลือกวันที่ให้ครบ";
  if (end.isBefore(start)) return "วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่มต้น";
  if (end.diff(start, "days") + 1 > MAX_RANGE_DAYS) {
    return `เลือกได้ไม่เกิน ${MAX_RANGE_DAYS} วันต่อครั้ง`;
  }
  return null;
}

// A rate doc's currencies as [code, rate] pairs sorted by code, without the
// doc's timeCreate.
export function sortedRates(data) {
  return Object.keys(data || {})
    .filter((key) => key !== "timeCreate" && data[key] && typeof data[key] === "object")
    .sort((a, b) => a.localeCompare(b))
    .map((code) => [code, data[code]]);
}

// Firestore Timestamp, Date or nothing -> "YYYY-MM-DD HH:mm" (local time).
export function formatUpdatedAt(timeCreate) {
  if (!timeCreate) return "";
  const date = typeof timeCreate.toDate === "function" ? timeCreate.toDate() : timeCreate;
  const m = moment(date);
  return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : "";
}

// docs: [{ id: "YYYY-MM-DD", data: <Firestore doc data> }]
// Returns the sheet as an array of arrays: header row, then one row per day
// per currency, sorted by date then currency. Days with no doc have no rows.
export function buildRateSheet(docs, columns) {
  const header = ["วันที่", "สกุลเงิน"]
    .concat(columns.map((c) => `${c.type === "BUYING" ? "ซื้อ" : "ขาย"} ${c.colName}`))
    .concat(["อัปเดตล่าสุด"]);
  const rows = [];
  docs
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
    .forEach(({ id, data }) => {
      const updatedAt = formatUpdatedAt(data && data.timeCreate);
      sortedRates(data).forEach(([currency, rate]) => {
        rows.push(
          [id, currency]
            .concat(columns.map((c) => parseRate(rate[c.fieldName])))
            .concat([updatedAt])
        );
      });
    });
  // Indexes of the rate columns, for number formatting.
  const rateCols = columns.map((_c, i) => i + 2);
  return { header, rows, rateCols };
}

// The layout of SAP's Exchange Rates table: one row per day, one column per
// currency, each cell the bank's primary buying rate.
export function buildSapSheet(docs, column) {
  const sorted = docs.slice().sort((a, b) => a.id.localeCompare(b.id));
  const codes = Array.from(
    new Set([].concat(...sorted.map(({ data }) => sortedRates(data).map(([code]) => code))))
  ).sort((a, b) => a.localeCompare(b));
  const header = ["วันที่"].concat(codes);
  const rows = sorted.map(({ id, data }) =>
    [id].concat(codes.map((code) => (data && data[code] ? parseRate(data[code][column.fieldName]) : null)))
  );
  const rateCols = codes.map((_c, i) => i + 1);
  return { header, rows, rateCols };
}

export function exportFileName(bankCode, from, to, format) {
  const suffix = format === "sap" ? "_sap" : "";
  return `exchange-rate_${bankCode}_${from}_${to}${suffix}.xlsx`;
}
